import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { feedService, discussionService } from '../services/api';
import { Card, StatCard, Button, Spinner, EmptyState } from '../components/ui';
import { useNotification } from '../contexts/NotificationContext';

/** A teacher's single inbox for "things students said that might need a
 * reply": course-post comments and lesson-discussion questions/replies,
 * merged and sorted by most recent activity. Reply inline right here, or
 * follow "View in context" to the actual post/lesson. Opening this page
 * marks the backlog seen, clearing the sidebar badge - see
 * feed.views.teacher_engagement_* on the backend. */
function TeacherComments() {
  const { t, i18n } = useTranslation();
  const { addNotification } = useNotification();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [sendingKey, setSendingKey] = useState(null);
  const [repliedKeys, setRepliedKeys] = useState(new Set());

  const itemKey = (item) => `${item.type}-${item.type === 'comment' ? item.post_id : item.thread_id}`;

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([feedService.getTeacherEngagementSummary(), feedService.getTeacherEngagementItems()])
      .then(([summaryRes, itemsRes]) => {
        setSummary(summaryRes.data);
        setItems(itemsRes.data);
      })
      .catch(() => addNotification(t('Failed to load comments and questions'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    feedService.markTeacherEngagementSeen().catch(() => {});
    load();
  }, [load]);

  const updateDraft = (key, value) => setDrafts((prev) => ({ ...prev, [key]: value }));

  const handleReply = async (item) => {
    const key = itemKey(item);
    const content = (drafts[key] || '').trim();
    if (!content) return;
    setSendingKey(key);
    try {
      if (item.type === 'comment') {
        await feedService.addComment(item.post_id, content);
      } else {
        await discussionService.createReply({ thread: item.thread_id, body: content });
      }
      setRepliedKeys((prev) => new Set(prev).add(key));
      updateDraft(key, '');
      addNotification(t('Reply sent'), 'success');
    } catch (error) {
      addNotification(t('Failed to send your reply'), 'error');
    } finally {
      setSendingKey(null);
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Comments & Questions')} subtitle={t('Everything students said on your courses, in one inbox')} />
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          {summary && (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard label={t('Total comments')} value={summary.total_comments} color="blue" />
              <StatCard label={t('Total likes')} value={summary.total_likes} color="purple" />
              <StatCard label={t('Questions asked')} value={summary.total_questions} color="yellow" />
              <StatCard label={t('Unread')} value={summary.unread_count} color="green" />
            </div>
          )}

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : items.length === 0 ? (
            <Card><EmptyState title={t('Nothing here yet')} description={t('New comments and questions on your courses will show up here.')} /></Card>
          ) : (
            <div className="space-y-3">
              {items.map((item) => {
                const key = itemKey(item);
                const replied = repliedKeys.has(key);
                return (
                  <Card key={key} className={`p-4 ${item.is_unread && !replied ? 'ring-2 ring-accent-200' : ''}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-gray-900">{item.author_name}</span>
                          {item.is_unread && !replied && (
                            <span className="w-2 h-2 rounded-full bg-accent-500 shrink-0" title={t('Unread')} />
                          )}
                          <span className="text-xs text-gray-400 truncate">
                            {item.module_name}{item.lesson_name ? ` · ${item.lesson_name}` : ''}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap break-words">{item.content}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(item.created_at))}
                        </p>
                      </div>
                      <Link to={item.target_url} className="text-xs font-semibold text-primary-600 hover:underline shrink-0">
                        {t('View in context')}
                      </Link>
                    </div>

                    {replied ? (
                      <p className="mt-3 text-xs font-semibold text-emerald-600">{t('Reply sent')}</p>
                    ) : (
                      <div className="mt-3 flex gap-2">
                        <input
                          value={drafts[key] || ''}
                          onChange={(e) => updateDraft(key, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleReply(item); }}
                          placeholder={t('Write a reply…')}
                          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none"
                        />
                        <Button
                          size="sm"
                          loading={sendingKey === key}
                          disabled={!(drafts[key] || '').trim()}
                          onClick={() => handleReply(item)}
                        >
                          {t('Reply')}
                        </Button>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default TeacherComments;
